import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter } from '@mantine/core';
import { Link } from 'atomic-router-react';
import clsx from 'clsx';
import { StoreValue } from 'effector';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { accountTreeModel } from 'entities/account';
import { PLAYER_TRANSACTION_TYPE } from 'shared/api';
import { TIME_ZONES } from 'shared/config';
import { $t, translated } from 'shared/lib';
import {
	accountRoute,
	cashdeskRoute,
	deviceRoute,
	gameSettingsRoute,
	playerRoute,
	userRoute,
} from 'shared/routing';
import { createDateFilter, createRegistry, MultiSelect, RegistryRootProps } from 'shared/ui';
import { PlayerTransactionRegistryModel } from '../../model';
import cls from './player-transaction-registry.module.scss';

export type PlayerTransactionRegistryVariant = 'full' | 'compact';

export type PlayerTransactionRegistryProps = RegistryRootProps & {
	emptyActionSlot?: ReactNode;
	topBarActionSlot?: ReactNode;
	withTitle?: boolean;
};

export type CreatePlayerTransactionRegistryOptions = {
	variant?: PlayerTransactionRegistryVariant;
};

export const createPlayerTransactionRegistry = (
	{ registryModel, dateFilterModel, ...model }: PlayerTransactionRegistryModel,
	{ variant = 'full' }: CreatePlayerTransactionRegistryOptions = {},
) => {
	const compact = variant === 'compact';

	const DateFilter = createDateFilter(dateFilterModel);

	const DateField = reflect({
		view: DateFilter.DateField,
		bind: {
			'data-test-id': 'player-transaction-registry-date-input',
			className: cls.playerTransactionRegistryDate,
		},
	});

	const TimeZoneField = reflect({
		view: DateFilter.TimeZoneField,
		bind: {
			'data-test-id': 'player-transaction-registry-time-zone-filter',
			className: cls.playerTransactionRegistryTimeZone,
			value: registryModel.filters.timeZone.$value.map((tz) => tz.toString()),
			onChange: (value) =>
				registryModel.filters.timeZone.setValue(Number(value ?? TIME_ZONES['UTC-00:00'])),
		},
	});

	const CashdesksField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-transaction-registry-cashdesks-filter',
			value: registryModel.filters.cashdeskIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => registryModel.filters.cashdeskIds.setValue(value.map(Number)),
			className: cls.playerTransactionRegistryMultiSelect,
			data: model.$cashdesks,
			label: translated('player-transaction-registry.filters.cashdesks.label'),
			placeholder: translated('player-transaction-registry.filters.cashdesks.placeholder'),
			nothingFoundMessage: translated('player-transaction-registry.filters.cashdesks.empty'),
			searchable: true,
		},
	});

	const DevicesField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-transaction-registry-devices-filter',
			value: registryModel.filters.deviceIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => registryModel.filters.deviceIds.setValue(value.map(Number)),
			className: cls.playerTransactionRegistryMultiSelect,
			data: model.$devices,
			label: translated('player-transaction-registry.filters.devices.label'),
			placeholder: translated('player-transaction-registry.filters.devices.placeholder'),
			nothingFoundMessage: translated('player-transaction-registry.filters.devices.empty'),
			searchable: true,
		},
	});

	const PlayersField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-transaction-registry-players-filter',
			value: registryModel.filters.playerIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => registryModel.filters.playerIds.setValue(value.map(Number)),
			className: cls.playerTransactionRegistryMultiSelect,
			data: model.$players,
			label: translated('player-transaction-registry.filters.players.label'),
			placeholder: translated('player-transaction-registry.filters.players.placeholder'),
			nothingFoundMessage: translated('player-transaction-registry.filters.players.empty'),
			searchable: true,
		},
	});

	const GamesField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-transaction-registry-games-filter',
			value: registryModel.filters.gameIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => registryModel.filters.gameIds.setValue(value.map(Number)),
			className: cls.playerTransactionRegistryMultiSelect,
			data: model.$games,
			label: translated('player-transaction-registry.filters.games.label'),
			placeholder: translated('player-transaction-registry.filters.games.placeholder'),
			nothingFoundMessage: translated('player-transaction-registry.filters.games.empty'),
			searchable: true,
		},
	});

	const CashiersField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-transaction-registry-cashiers-filter',
			value: registryModel.filters.cashierIds.$value.map((ids) => ids.map(String)),
			onChange: (value) => registryModel.filters.cashierIds.setValue(value.map(Number)),
			className: cls.playerTransactionRegistryMultiSelect,
			data: model.$cashiers,
			label: translated('player-transaction-registry.filters.cashiers.label'),
			placeholder: translated('player-transaction-registry.filters.cashiers.placeholder'),
			nothingFoundMessage: translated('player-transaction-registry.filters.cashiers.empty'),
			searchable: true,
		},
	});

	const CurrenciesField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-transaction-registry-currencies-filter',
			value: registryModel.filters.currencies.$value,
			onChange: registryModel.filters.currencies.setValue,
			className: cls.playerTransactionRegistryMultiSelect,
			data: accountTreeModel.$currencies,
			label: translated('player-transaction-registry.filters.currencies.label'),
			placeholder: translated('player-transaction-registry.filters.currencies.placeholder'),
			nothingFoundMessage: translated('player-transaction-registry.filters.currencies.empty'),
			searchable: true,
		},
	});

	const TypesField = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'player-transaction-registry-types-filter',
			value: registryModel.filters.types.$value,
			onChange: registryModel.filters.types.setValue,
			className: clsx(cls.playerTransactionRegistryMultiSelect, cls.playerTransactionRegistryTypes),
			data: $t.map((t) =>
				Object.values(PLAYER_TRANSACTION_TYPE).map((type) => ({
					value: type,
					label: t(`player-transaction-registry.types.${type}`, { defaultValue: type }),
				})),
			),
			label: translated('player-transaction-registry.filters.types.label'),
			placeholder: translated('player-transaction-registry.filters.types.placeholder'),
			nothingFoundMessage: translated('player-transaction-registry.filters.types.empty'),
			searchable: true,
		},
	});

	const AccountLink = reflect({
		view: Anchor<typeof Link>,
		bind: {
			'data-test-id': 'player-transaction-registry-account-link',
			to: accountRoute,
			component: Link,
		},
	});

	const CashdeskLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof cashdeskRoute.$params>>>,
		bind: {
			'data-test-id': 'player-transaction-registry-cashdesk-link',
			to: cashdeskRoute,
			component: Link,
		},
	});

	const DeviceLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof deviceRoute.$params>>>,
		bind: {
			'data-test-id': 'player-transaction-registry-device-link',
			to: deviceRoute,
			component: Link,
		},
	});

	const PlayerLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof playerRoute.$params>>>,
		bind: {
			'data-test-id': 'player-transaction-registry-player-link',
			to: playerRoute,
			component: Link,
		},
	});

	const GameLink = reflect({
		view: Anchor<typeof Link>,
		bind: {
			'data-test-id': 'player-transaction-registry-game-link',
			to: gameSettingsRoute,
			component: Link,
		},
	});

	const CashierLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof userRoute.$params>>>,
		bind: {
			'data-test-id': 'player-transaction-registry-cashier-link',
			to: userRoute,
			component: Link,
		},
	});

	const Registry = createRegistry(registryModel);

	const PlayerTransactionRegistry = forwardRef<HTMLDivElement, PlayerTransactionRegistryProps>(
		({ topBarActionSlot, emptyActionSlot, withTitle = true, className, ...props }, ref) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
					className={clsx(className, cls.playerTransactionRegistry)}
					data-compact={compact || undefined}
				>
					{withTitle && <Registry.Title>{t('player-transaction-registry.title')}</Registry.Title>}
					<Registry.TopBar
						alwaysShow
						className={cls.playerTransactionRegistryTopBar}
					>
						<Registry.Filters
							visibleFrom='md'
							classNames={{
								root: cls.playerTransactionRegistryFilters,
								inner: cls.playerTransactionRegistryFiltersInner,
							}}
						>
							<div className={cls.playerTransactionRegistryFiltersGroup}>
								<DateField />
								<TimeZoneField />
							</div>
							{compact ? (
								<div className={cls.playerTransactionRegistryFiltersGroup}>
									<CashiersField />
									<CurrenciesField />
									<TypesField />
								</div>
							) : (
								<>
									<div className={cls.playerTransactionRegistryFiltersGroup}>
										<CashdesksField />
										<DevicesField />
										<PlayersField />
									</div>
									<div className={cls.playerTransactionRegistryFiltersGroup}>
										<GamesField />
										<CashiersField />
										<CurrenciesField />
										<TypesField />
									</div>
								</>
							)}
						</Registry.Filters>
						{topBarActionSlot}
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							<Registry.Table.Col className={cls.playerTransactionRegistryColDate} />
							{!compact && (
								<>
									<Registry.Table.Col />
									<Registry.Table.Col />
									<Registry.Table.Col />
								</>
							)}
							<Registry.Table.Col />
							{!compact && <Registry.Table.Col />}
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col className={cls.playerTransactionRegistryColBalanceAfter} />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.ThSorter field='date'>
									{t('player-transaction-registry.table-columns.date')}
								</Registry.Table.ThSorter>
								{!compact && (
									<>
										<Registry.Table.ThSorter field='account'>
											{t('player-transaction-registry.table-columns.account')}
										</Registry.Table.ThSorter>
										<Registry.Table.ThSorter field='cashdesk'>
											{t('player-transaction-registry.table-columns.cashdesk')}
										</Registry.Table.ThSorter>
										<Registry.Table.ThSorter field='device'>
											{t('player-transaction-registry.table-columns.device')}
										</Registry.Table.ThSorter>
									</>
								)}
								<Registry.Table.ThSorter field='player'>
									{t('player-transaction-registry.table-columns.player')}
								</Registry.Table.ThSorter>
								{!compact && (
									<Registry.Table.ThSorter field='game'>
										{t('player-transaction-registry.table-columns.game')}
									</Registry.Table.ThSorter>
								)}
								<Registry.Table.ThSorter field='cashier'>
									{t('player-transaction-registry.table-columns.cashier')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='currency'>
									{t('player-transaction-registry.table-columns.currency')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='type'>
									{t('player-transaction-registry.table-columns.type')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='amount'>
									{t('player-transaction-registry.table-columns.amount')}
								</Registry.Table.ThSorter>
								<Registry.Table.Th>
									{t('player-transaction-registry.table-columns.balance-before')}
								</Registry.Table.Th>
								<Registry.Table.Th>
									{t('player-transaction-registry.table-columns.balance-after')}
								</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList>
								{(row) => (
									<>
										<Registry.Table.Td>{row.date}</Registry.Table.Td>
										{!compact && (
											<>
												<Registry.Table.Td>
													{row.account && (
														<AccountLink query={{ id: row.account.id }}>
															{row.account.name}
														</AccountLink>
													)}
												</Registry.Table.Td>
												<Registry.Table.Td>
													{row.cashdesk && (
														<CashdeskLink params={{ id: row.cashdesk.id }}>
															{row.cashdesk.name}
														</CashdeskLink>
													)}
												</Registry.Table.Td>
												<Registry.Table.Td>
													{row.device && (
														<DeviceLink params={{ id: row.device.id }}>
															{row.device.name}
														</DeviceLink>
													)}
												</Registry.Table.Td>
											</>
										)}
										<Registry.Table.Td>
											{row.player && (
												<PlayerLink
													params={{ id: row.player.id }}
													query={compact ? undefined : { tab: 'transactions' }}
												>
													{row.player.name}
												</PlayerLink>
											)}
										</Registry.Table.Td>
										{!compact && (
											<Registry.Table.Td>
												{row.game && (
													<GameLink query={{ id: row.game.id }}>{row.game.title}</GameLink>
												)}
											</Registry.Table.Td>
										)}
										<Registry.Table.Td>
											{row.cashier && (
												<CashierLink params={{ id: row.cashier.id }}>
													{row.cashier.name}
												</CashierLink>
											)}
										</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td className={cls.playerTransactionRegistryType}>
											{t(`player-transaction-registry.types.${row.type}`, {
												defaultValue: row.type,
											})}
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.amount} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											{row.balanceBefore !== null && <NumberFormatter value={row.balanceBefore} />}
										</Registry.Table.Td>
										<Registry.Table.Td>
											{row.balanceAfter !== null && <NumberFormatter value={row.balanceAfter} />}
										</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('player-transaction-registry.empty.title')}
						message={t('player-transaction-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('player-transaction-registry.table-empty.title')}
						message={t('player-transaction-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return PlayerTransactionRegistry;
};
